import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import type { ListingDetail } from "@cd/core";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Dialog } from "../../components/ui/overlay";
import { Input, Label, Select } from "../../components/ui/primitives";

const NEW = "__new__";

/** A dashboard installs as a new dashboard; a widget goes onto one of yours or a new one. */
export function InstallDialog({ open, onOpenChange, listing }: { open: boolean; onOpenChange: (o: boolean) => void; listing: ListingDetail }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const isWidget = listing.kind === "widget";
  const [title, setTitle] = useState(listing.title);
  const [target, setTarget] = useState(NEW);
  const dashboards = useQuery({ queryKey: ["dashboards"], queryFn: api.dashboards, enabled: open && isWidget });
  const mine = dashboards.data?.filter((d) => !d.sharedBy) ?? [];

  useEffect(() => {
    if (open) {
      setTitle(listing.title);
      setTarget(NEW);
    }
  }, [open, listing.title]);

  const install = useMutation({
    mutationFn: () => api.install(listing.id, target === NEW ? { newDashboard: title.trim() } : { dashboardId: target }),
    onSuccess: ({ dashboardId }) => {
      qc.invalidateQueries({ queryKey: ["dashboards"] });
      qc.invalidateQueries({ queryKey: ["dashboard", dashboardId] });
      qc.invalidateQueries({ queryKey: ["marketplace"] });
      qc.invalidateQueries({ queryKey: ["listing", listing.id] });
      onOpenChange(false);
      void navigate({ to: "/d/$id", params: { id: dashboardId } });
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={`Install ${listing.title}`} className="w-[440px] max-w-[calc(100vw-32px)] p-6">
      <h2 className="text-base font-semibold tracking-tight">Install “{listing.title}”</h2>
      <p className="mt-1 text-sm text-muted">
        You get your own copy to edit. It shows data for your locations only. When the publisher releases a new version, you choose whether to update it.
      </p>
      <div className="mt-5 space-y-4">
        {isWidget && (
          <div>
            <Label htmlFor="install-target">Add to</Label>
            <Select id="install-target" value={target} onChange={(e) => setTarget(e.target.value)} className="mt-1 w-full">
              <option value={NEW}>New dashboard</option>
              {mine.map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}
            </Select>
          </div>
        )}
        {target === NEW && (
          <div>
            <Label htmlFor="install-title">Dashboard name</Label>
            <Input id="install-title" value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)} className="mt-1" />
          </div>
        )}
      </div>
      {install.error && <p className="mt-4 text-sm text-bad">{install.error.message}</p>}
      <div className="mt-6 flex justify-end gap-2">
        <Button onClick={() => onOpenChange(false)}>Cancel</Button>
        <Button variant="primary" disabled={install.isPending || (target === NEW && !title.trim())} onClick={() => install.mutate()}>
          Install
        </Button>
      </div>
    </Dialog>
  );
}
